import {
  AddIcon,
  Button,
  FlexContent,
  Form,
  FormField,
  ModalDialog,
  Select,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import Icon from "../../../../components/ui/Icon";
import Table from "../../../../components/ui/Table";
import NotificationDispatch, {
  showErrorNotification,
  showInformativeNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminFastener, AdminFastenerUsage, FastenerUsageType } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";
import { getDeleteAction } from "../../../../utils/rowActions";

interface Option {
  value: string;
  label: string;
}

type Scope = "families" | "fasteners";

interface FormBody {
  scope: Option | null;
  families: Option[];
  fasteners: Option[];
  exceptions: Option[];
  usage: Option | null;
}

const USAGE_VALUES: FastenerUsageType[] = ["standard", "inclined"];

/** Family of a fastener: its screw_family, or (when unset) the letters before the size in its code. */
const familyOf = (fastener: AdminFastener): string =>
  fastener.screwFamily ?? fastener.item.code.match(/^[A-Za-z]+/)?.[0].toUpperCase() ?? fastener.item.code;

/** Fasteners the form targets (the picked ones, or the picked families minus the exceptions) and the exceptions within those families. */
const resolveTargets = (
  form: FormBody,
  catalog: AdminFastener[],
): { targets: AdminFastener[]; exceptions: AdminFastener[] } => {
  if (form.scope?.value === "fasteners") {
    const picked = new Set(form.fasteners.map((x) => x.value));
    return { targets: catalog.filter((x) => picked.has(x.item.id)), exceptions: [] };
  }
  const families = new Set(form.families.map((x) => x.value));
  const excepted = new Set(form.exceptions.map((x) => x.value));
  const inFamilies = catalog.filter((x) => families.has(familyOf(x)));
  return {
    targets: inFamilies.filter((x) => !excepted.has(x.item.id)),
    exceptions: inFamilies.filter((x) => excepted.has(x.item.id)),
  };
};

/**
 * Fastener ↔ usage compatibility table (lock_calculator.fastener_usage): which
 * behaviours (standard header/joist, inclined) each fastener may take. The
 * backend filters read it, so a fastener with no row is selectable nowhere.
 */
const AdminFastenerUsagesTab: FC = () => {
  const i18n = useI18n();
  const t = i18n.translation.lockCalculatorModule.admin.fastenerUsages;
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "fastenerUsage" });
  const aborter = useRef(new AbortController());
  const query: PaginationQuery = { ...filters.state, q: filters.state.q };
  const { data, error } = adminApi.useFastenerUsages(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminFastenerUsage>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const usageLabel = (usage: FastenerUsageType) => (usage === "inclined" ? t.usageInclined : t.usageStandard);

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminFastenerUsage>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}>
              <Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{t.fastener}</TableHeadCell>
              <TableHeadCell align="left">{t.usage}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.fastener.code}</TableCell>
              <TableCell>{usageLabel(item.usage)}</TableCell>
            </>
          )}
          rowActions={(item) => [getDeleteAction(i18n, () => setItemToDelete(item), true)]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={t.singular}
            isSubmitting={isSubmitting}
            buttons={[
              <Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">
                {i18n.translation.common.cancel}
              </Button>,
              <Button
                key={1}
                onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteFastenerUsage(itemToDelete.fastener.id, itemToDelete.usage)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}
              >
                {i18n.translation.common.yes}
              </Button>,
            ]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
    </>
  );
};

/** Families (+ exceptions) or single fasteners, depending on the chosen scope. */
const TargetFields: FC<{ control: Control<FormBody>; catalog: AdminFastener[] }> = ({ control, catalog }) => {
  const i18n = useI18n();
  const t = i18n.translation.lockCalculatorModule.admin.fastenerUsages;
  const scope = useWatch({ control, name: "scope" })?.value as Scope | undefined;
  const selectedFamilies = useWatch({ control, name: "families" });

  const fastenerOptions = useMemo<Option[]>(
    () => catalog.map((x) => ({ value: x.item.id, label: x.item.code })),
    [catalog],
  );
  const familyOptions = useMemo<Option[]>(() => {
    const counts = new Map<string, number>();
    catalog.forEach((x) => counts.set(familyOf(x), (counts.get(familyOf(x)) ?? 0) + 1));
    return [...counts.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([family, count]) => ({ value: family, label: `${family} (${count})` }));
  }, [catalog]);
  const exceptionOptions = useMemo<Option[]>(() => {
    const families = new Set(selectedFamilies.map((x) => x.value));
    return catalog.filter((x) => families.has(familyOf(x))).map((x) => ({ value: x.item.id, label: x.item.code }));
  }, [catalog, selectedFamilies]);

  const multiSelect = (
    name: "families" | "fasteners" | "exceptions",
    label: string,
    options: Option[],
    isRequired: boolean,
  ) => (
    <Controller
      name={name}
      control={control}
      rules={{ validate: (value) => !isRequired || value.length > 0 }}
      render={({ field, fieldState }) => (
        <FormField label={label} error={fieldState.error}>
          {({ labelId }) => (
            <Select<Option, true>
              inputId={labelId}
              options={options}
              isMulti
              placeholder={`${i18n.translation.common.select}...`}
              getOptionLabel={(x) => x.label}
              getOptionValue={(x) => x.value}
              isInvalid={fieldState.invalid}
              onChange={(options) => field.onChange(Array.from(options))}
              value={field.value}
            />
          )}
        </FormField>
      )}
    />
  );

  if (scope === "fasteners") return multiSelect("fasteners", t.fasteners, fastenerOptions, true);
  return (
    <>
      {multiSelect("families", t.families, familyOptions, true)}
      {multiSelect("exceptions", t.exceptions, exceptionOptions, false)}
    </>
  );
};

const FormModal: FC<{ onClose: () => void }> = ({ onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.lockCalculatorModule.admin.fastenerUsages;
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();
  const [catalog, setCatalog] = useState<AdminFastener[]>([]);

  useEffect(() => {
    const aborter = new AbortController();
    adminApi
      .getFasteners({ q: "", page: 1, limit: 400 }, aborter.signal)
      .then(setCatalog)
      .catch((err) => {
        if (!aborter.signal.aborted) dispatch(showErrorNotification(i18n, err));
      });
    return () => aborter.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usageOptions: Option[] = USAGE_VALUES.map((usage) => ({
    value: usage,
    label: usage === "inclined" ? t.usageInclined : t.usageStandard,
  }));

  const scopeOptions: Option[] = [
    { value: "families" satisfies Scope, label: t.scopeFamilies },
    { value: "fasteners" satisfies Scope, label: t.scopeFasteners },
  ];

  const onSubmit = () => async (formData: FormBody) => {
    if (!formData.usage?.value) return;
    const usage = formData.usage.value as FastenerUsageType;
    const { targets, exceptions } = resolveTargets(formData, catalog);
    const hasUsage = (x: AdminFastener) => x.usages?.includes(usage) ?? false;
    const alreadyPresent = targets.filter(hasUsage);
    const toAdd = targets.filter((x) => !hasUsage(x));
    // An exception that already has the usage was added earlier: it gets removed.
    const toRemove = exceptions.filter(hasUsage);
    if (alreadyPresent.length + toAdd.length + toRemove.length === 0) {
      dispatch(showInformativeNotification(t.resultNothingToDo, false));
      onClose();
      return;
    }

    const results = await Promise.allSettled([
      ...toAdd.map((x) => adminApi.insertFastenerUsage({ fastenerItemId: x.item.id, usage })),
      ...toRemove.map((x) => adminApi.deleteFastenerUsage(x.item.id, usage)),
    ]);
    const failed = results.find((x): x is PromiseRejectedResult => x.status === "rejected");
    if (failed) {
      dispatch(showErrorNotification(i18n, failed.reason));
      return;
    }

    const codes = (items: AdminFastener[]) => items.map((x) => x.item.code).join(", ");
    const summary = [
      alreadyPresent.length > 0 && `${t.resultAlreadyPresent}: ${codes(alreadyPresent)}`,
      toAdd.length > 0 && `${t.resultAdded}: ${codes(toAdd)}`,
      toRemove.length > 0 && `${t.resultRemoved}: ${codes(toRemove)}`,
    ].filter((x): x is string => !!x);
    dispatch(showSuccessNotification(i18n.translation, summary.join(". "), true));
    onClose();
  };

  return (
    <ModalDialog onClose={onClose} title={`${i18n.translation.common.add} ${t.singular}`}>
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={{ scope: scopeOptions[0], families: [], fasteners: [], exceptions: [], usage: null }}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="scope"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={t.scope} error={fieldState.error}>
                  {({ labelId }) => (
                    <Select<Option, false>
                      inputId={labelId}
                      options={scopeOptions}
                      isMulti={false}
                      isClearable={false}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                      value={field.value}
                    />
                  )}
                </FormField>
              )}
            />
            <TargetFields control={control} catalog={catalog} />
            <Controller
              name="usage"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={t.usage} error={fieldState.error}>
                  {({ labelId }) => (
                    <Select<Option, false>
                      inputId={labelId}
                      options={usageOptions}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                      value={field.value}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminFastenerUsagesTab;
